import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { useTravel } from 'v3/pages/Travel';
import {
  Area,
  AreaChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

import { Text } from 'v3/components';

const getFirstValue = (source, fieldNames = []) => {
  for (const fieldName of fieldNames) {
    const value = source?.[fieldName];

    if (value !== undefined && value !== null && value !== '') return value;
  }

  return null;
};

const normalizeTemperature = (value) => {
  if (value === null || value === undefined) return null;
  if (value === 'N') return null;

  const normalized = Number(String(value).replace(',', '.'));

  return Number.isNaN(normalized) ? null : normalized;
};

const getTemperatureDate = (temp) => {
  const date = getFirstValue(temp, [
    'datetimeTemperature',
    'datetime_temperature',
    'datetimeTemperature1',
    'datetime_temperature_1',
    'captureData',
    'captured_at',
    'createdAt',
    'created_at',
  ]);

  if (date && moment(date).isValid()) {
    return moment(date).format('HH:mm');
  }

  return date;
};

function Temperature({ handleClickTemperature, setLatLngTemperature }) {
  const travel = useTravel();

  const [data, setData] = useState([]);
  const hasTemperatureData = data.some(
    (item) =>
      item.Temperatura1 !== null ||
      item.Temperatura2 !== null ||
      item.Temperatura3 !== null ||
      item.Temperatura4 !== null,
  );

  useEffect(() => {
    const rawTemperatureData = travel?.data?.loadTemperature || [];
    const mergedData = rawTemperatureData.map((temp) => {
      return {
        captureData: getTemperatureDate(temp),
        Temperatura1: normalizeTemperature(getFirstValue(temp, ['temperature1', 'temperature_1', 'temperatura1'])),
        Temperatura2: normalizeTemperature(getFirstValue(temp, ['temperature2', 'temperature_2', 'temperatura2'])),
        Temperatura3: normalizeTemperature(getFirstValue(temp, ['temperature3', 'temperature_3', 'temperatura3'])),
        Temperatura4: normalizeTemperature(getFirstValue(temp, ['temperature4', 'temperature_4', 'temperatura4'])),
        datetimeTemperature1: getFirstValue(temp, ['datetimeTemperature1', 'datetime_temperature_1']),
        datetimeTemperature2: getFirstValue(temp, ['datetimeTemperature2', 'datetime_temperature_2']),
        datetimeTemperature3: getFirstValue(temp, ['datetimeTemperature3', 'datetime_temperature_3']),
        datetimeTemperature4: getFirstValue(temp, ['datetimeTemperature4', 'datetime_temperature_4']),
        latitude: getFirstValue(temp, ['latitudeTemperature', 'latitude_temperature', 'latitude', 'lat']),
        longitude: getFirstValue(temp, ['longitudeTemperature', 'longitude_temperature', 'longitude', 'lng', 'lon']),
        point: temp.id,
      };
    });

    setData(mergedData);
  }, [travel?.data?.loadTemperature]);

  function onClick(chartData) {
    const activeTooltipIndex = Number(chartData?.activeTooltipIndex);
    const payload =
      chartData?.activePayload?.[0]?.payload ||
      (!Number.isNaN(activeTooltipIndex) ? data[activeTooltipIndex] : null);
    if (!payload) return;

    setLatLngTemperature(payload);
    handleClickTemperature(payload);
  }

  return (
    <>
      <Text type="medium" style={{ fontStyle: 'italic' }}>
        Temperatura obtida via Raster
      </Text>
      {hasTemperatureData ? (
        <ResponsiveContainer width="101%" height={200}>
          <AreaChart
            width={730}
            height={160}
            onClick={onClick}
            data={data}
            margin={{ top: 10, right: 20, left: 20 }}
          >
            <XAxis
              dataKey="captureData"
              interval={0}
              minTickGap={0}
              tickFormatter={() => ''}
              tickLine
            />
            <YAxis unit="°C" />
            <CartesianGrid strokeDasharray="2 2" vertical horizontal />
            <Tooltip />
            <Legend />
            <Area
              type="monotone"
              dataKey="Temperatura1"
              stroke="#1C4B75"
              fill="#1C4B75"
              stackId="1"
              fillOpacity={0.3}
              style={{ cursor: 'pointer' }}
              unit="°C"
              onClick={onClick}
            />
            <Area
              type="monotone"
              dataKey="Temperatura2"
              stroke="#8511F2"
              fill="#8511F2"
              fillOpacity={0.3}
              stackId="1"
              style={{ cursor: 'pointer' }}
              unit="°C"
              onClick={onClick}
            />
            <Area
              type="monotone"
              dataKey="Temperatura3"
              stroke="#FF0345"
              fill="#FF0345"
              fillOpacity={0.3}
              stackId="1"
              style={{ cursor: 'pointer' }}
              unit="°C"
              onClick={onClick}
            />
            <Area
              type="monotone"
              dataKey="Temperatura4"
              stroke="#000000"
              fill="#000000"
              stackId="1"
              fillOpacity={0.3}
              style={{ cursor: 'pointer' }}
              unit="°C"
              onClick={onClick}
            />
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <Text color="gray">Não temos dados sobre a temperatura</Text>
      )}
    </>
  );
}

export default Temperature;
